import { inject, Injectable } from '@angular/core';
import { ApiRouteInspectionAssemblyItemService } from 'src/app/api/elite/routes/inspection-assembly-item.service';
import { ApiRouteInspectionMeasureService } from 'src/app/api/elite/routes/inspection-measure.service';
import { combineLatest, forkJoin, Observable } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import {
  InspectionAssemblyItem,
  InspectionMeasureTool,
  InspectionMeasureType,
} from 'src/app/api/elite/types/inspection-assembly-item';
import { RoutingService } from 'src/app/core/services/routing.service';
import { InspectionMeasureAssemblyItemDto } from 'src/app/api/elite/dto/inspection-measure-assembly-item.dto';
import { InspectionMeasureApiDto } from '../../../api/elite/dto/inspection-measure.dto';
import { ApiRouteSpecialLabelService } from '../../../api/elite/routes/special-label.service';

@Injectable({
  providedIn: 'root',
})
export class QualityControlInspectionDataViewApiService {
  private readonly apiInspectionAssemblyItemRouter = inject(
    ApiRouteInspectionAssemblyItemService
  );
  private readonly routerService = inject(RoutingService);
  private readonly apiInspectionMeasureRouter = inject(
    ApiRouteInspectionMeasureService
  );
  private readonly apiRouteSpecialLabelService = inject(
    ApiRouteSpecialLabelService
  );

  constructor() {}

  data(): Observable<{
    items: InspectionAssemblyItem[];
    types: InspectionMeasureType[];
  }> {
    return combineLatest([
      this.routerService.lastRouteData<string>('department').pipe(take(1)),
      this.routerService.lastRouteData<string>('transactionType').pipe(take(1)),
    ]).pipe(
      switchMap(([department, transactionType]) => {
        if (!department && !transactionType) {
          throw new Error('No department or transactionType');
        }
        const params = department ? { department } : { transactionType };
        return forkJoin({
          measures:
            this.apiInspectionAssemblyItemRouter.getItemsMeasures(params),
          measureTypes: this.apiInspectionMeasureRouter.typesOfMeasure(department),
        });
      }),
      map(({ measures, measureTypes }) => {
        const items = measures.map((el) => this.mapAssemblyItem(el));
        return {
          items,
          types: measureTypes,
        };
      })
    );
  }

  getAssemblyItem(itemName: string): Observable<InspectionAssemblyItem> {
    return this.apiInspectionAssemblyItemRouter.getMeasureByName(itemName);
  }

  add(
    data: InspectionMeasureAssemblyItemDto['create']['body']
  ): Observable<InspectionAssemblyItem> {
    return this.apiInspectionAssemblyItemRouter.addItem(data);
  }

  addMeasureTye(
    data: InspectionMeasureApiDto['addMeasureType']['body']
  ): Observable<InspectionMeasureType> {
    return this.apiInspectionMeasureRouter.addMeasureType(data);
  }

  addSpecialLabel(data: {
    title: string;
    shouldHavePartNumber: boolean;
  }): Observable<InspectionMeasureType> {
    return this.apiRouteSpecialLabelService.add(data);
  }

  update(
    id: number,
    data: InspectionMeasureAssemblyItemDto['update']['body']
  ): Observable<InspectionAssemblyItem> {
    return this.apiInspectionAssemblyItemRouter.updateItemMeasure(id, data);
  }

  toolTypes(): Observable<InspectionMeasureTool[]> {
    return this.apiInspectionMeasureRouter.getTools();
  }

  private mapAssemblyItem(
    item: InspectionAssemblyItem
  ): InspectionAssemblyItem {
    const mapItem = { ...item };
    item.measureValues.forEach((measureValue) => {
      for (const [key, value] of Object.entries(measureValue)) {
        if (key === 'measureTypeId' || key === 'type') {
          continue;
        }
        const column = this.toCamelCase(
          `${measureValue.measureType.title} ${key}`
        );
        mapItem[column] = value;
      }
    });
    return mapItem;
  }

  private toCamelCase(str: string): string {
    return str
      .split(' ')
      .map((word, index) => {
        // first word make sure to lowercase all the chars.
        if (index === 0) {
          return word.toLowerCase();
        }
        // upper case the first char and lowercase the rest.
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join('');
  }
}
